import { StandardSection } from "components/templates/StandardSection";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { MapConditions, MapHeader, MapSubtitle } from "./styles";
import { MobileOnly, NotMobile } from "components/templates/Breakpoints";
import { RewardsMapDesktop } from "components/organisms/RewardsMapDesktop";
import { RewardsMapMobile } from "components/organisms/RewardsMapMobile";
import { Anchor } from "components/atoms/Anchor";
import { StaticRoutes } from "@routes";
import { color } from "@styles";
import { getContinentFromId, getCountryDetails, getMapSize } from "./helpers";
import { Logger } from "services/logger";
import { CountryDetails, Reward, RewardCountry } from "./types";
import { serverSideAPIRequestService } from "services/serverSideApiRequest";
import { sanitiseMapRewards } from "utils/rewards";
import * as countryCodes from "./CountryCodes/index";

interface GlobalMapSectionProps {
  rewardCountries: RewardCountry[];
  rewardRegion: string;
  initialRewards: Reward[];
  title?: string | React.ReactNode;
  copy?: string | React.ReactNode;
  pb?: string;
  pt?: string;
}

/** TODO: split this out into an organism */
const GlobalMapSection: FC<GlobalMapSectionProps> = ({
  rewardCountries,
  rewardRegion,
  initialRewards,
  pb = "0",
  pt,
  title = `Explore our global member rewards and benefits`,
  copy = `We’re always working to bring your people top-tier rewards that motivate and spark
          excitement. Click on a region below to see what local benefits, discounts and rewards
          YuLife can offer your team.`,
}) => {
  const [selectedContinent, setSelectedContinent] = useState<string>(
    getContinentFromId(rewardRegion),
  );
  const [selectedCountry, setSelectedCountry] = useState<string | null>(rewardRegion);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });
  const [countryRewards, setCountryRewards] = useState<Reward[] | null>(initialRewards);
  const [countryDetails, setCountryDetails] = useState<CountryDetails | null>(
    getCountryDetails(rewardRegion),
  );
  const [sortedRewardCountries, setSortedRewardCountries] = useState(rewardCountries);
  const mapRef = useRef<HTMLDivElement>(null);

  const getAndSetMapSize = () => {
    if (mapRef.current) {
      const dimensions = getMapSize(mapRef);
      setMapDimensions(dimensions);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const resetCountryModalOnChange = () => {
    setSelectedCountry(null);
    closeModal();
  };

  const handleContinentClick = useCallback((continent: string) => {
    setSelectedContinent(continent);
    closeModal();
  }, []);

  const getCountryCodesByContinent = (continent: string) => {
    switch (continent) {
      case "Europe":
        return countryCodes.EUROPE_CODES;
      case "Africa":
        return countryCodes.AFRICA_CODES;
      case "Asia":
        return countryCodes.ASIA_CODES;
      case "North America":
        return countryCodes.NORTH_AMERICA_CODES;
      case "South America":
        return countryCodes.SOUTH_AMERICA_CODES;
      case "Oceania":
        return countryCodes.OCEANIA_CODES;
      default:
        return [];
    }
  };

  useEffect(() => {
    const correctCountryCodes = getCountryCodesByContinent(selectedContinent);
    const inContinent = rewardCountries.filter((country) =>
      correctCountryCodes.includes(country.countryCode),
    );
    const notInContinent = rewardCountries.filter(
      (country) => !correctCountryCodes.includes(country.countryCode),
    );
    setSortedRewardCountries([...inContinent, ...notInContinent]);
  }, [selectedContinent, rewardCountries]);

  const handleCountryClick = useCallback(async (countryId: string) => {
    try {
      const rewards = await serverSideAPIRequestService.getRewardsForCountryMap(countryId);
      const filteredRewards = sanitiseMapRewards(rewards, countryId);
      const countryDetails = getCountryDetails(countryId);

      setCountryRewards(filteredRewards);
      setSelectedCountry(countryId);
      setCountryDetails(countryDetails);
      setIsModalOpen(true);
    } catch (e) {
      Logger.error(e);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      getAndSetMapSize();
    };

    setTimeout(() => handleResize());

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [mapRef]);

  return (
    <StandardSection pb={pb} pt={pt}>
      {title && <MapHeader>{title}</MapHeader>}
      <NotMobile>
        {copy && <MapSubtitle>{copy}</MapSubtitle>}
        <RewardsMapDesktop
          mapRef={mapRef}
          selectedContinent={selectedContinent}
          selectedCountry={selectedCountry}
          handleCountryClick={handleCountryClick}
          handleContinentClick={handleContinentClick}
          rewardCountries={sortedRewardCountries}
          mapDimensions={mapDimensions}
          isModalOpen={isModalOpen}
          countryRewards={countryRewards}
          countryDetails={countryDetails}
          closeModal={closeModal}
          resetCountryModalOnChange={resetCountryModalOnChange}
        />
      </NotMobile>
      <MobileOnly>
        <RewardsMapMobile
          handleContinentClick={handleContinentClick}
          handleCountryClick={handleCountryClick}
          rewardCountries={sortedRewardCountries}
          selectedCountry={selectedCountry}
          countryDetails={countryDetails}
          countryRewards={countryRewards}
          resetCountryModalOnChange={resetCountryModalOnChange}
        />
      </MobileOnly>
      <MapConditions>
        Rewards and services are regularly updated and subject to change and pricing plan. 
      </MapConditions>
      <MapConditions>
        The YuLife app, including ESG partners, is globally available. EAP and VGP services vary per
        country, and where not fully available may require additional cost.
      </MapConditions>
      <MapConditions>
        Have a question about our global rewards, or a country of interest? Speak to the team{" "}
        <Anchor $isUnderlined href={StaticRoutes.contactUs} color={color.P100}>
          here
        </Anchor>
        .
      </MapConditions>
    </StandardSection>
  );
};

export default GlobalMapSection;
